import React, { useCallback, useState, useEffect, useRef } from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { StreamlitAppSignedUrlRequest } from 'libs/end-to-end/src/external-streamlit-apps';
import { StreamlitAppSignedUrlResponse } from 'libs/end-to-end/src/external-streamlit-apps';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { z } from 'zod';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { CircularProgress } from '@mui/material';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { isEqual } from 'lodash';

const MAX_AUTO_RECONNECT_ATTEMPTS = 3;

// Define the expected message schema from iframe
export const IframeMessageSchema = z.object({
  type: z.string(),
  payload: z.array(z.number()),
});

export type IframeMessage = z.infer<typeof IframeMessageSchema>;

// Check if a message from the iframe is a Streamlit error
export const isStreamlitErrorMessage = (data: unknown): boolean => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const message = data as Record<string, unknown>;

  if (typeof message.type !== 'string') {
    return false;
  }

  // CLIENT_ERROR only counts as error if error field is 401
  if (message.type === 'CLIENT_ERROR') {
    return message.error === 401;
  }

  // These types always count as errors
  if (message.type === 'CLIENT_ERROR_DIALOG') {
    return true;
  }

  return false;
};

// Helper to safely parse iframe messages
export const parseIframeMessage = (data: unknown): IframeMessage | null => {
  const result = IframeMessageSchema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  return null;
};

export const CustomAppsSection = ({
  organizationId,
  selectedAppId,
  databaseId,
  databaseRevisionId,
  customVizRequest,
  onMessage,
}: {
  organizationId: string | null;
  selectedAppId: string | null;
  databaseId: string | null;
  databaseRevisionId: string | null;
  customVizRequest: CustomVizRequest | null;
  onMessage?: (message: IframeMessage) => void;
}) => {
  const { userAuthenticatedApiCall } = useUser();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoadingSignedUrl, setIsLoadingSignedUrl] = useState(false);
  const [hasError, setHasError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const autoReconnectAttemptsRef = useRef(0);
  const autoReconnectRef = useRef<(() => void) | null>(null);

  // Listen for messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: verify the origin matches the iframe's URL
      if (signedUrl) {
        try {
          const iframeOrigin = new URL(signedUrl).origin;
          if (event.origin !== iframeOrigin) {
            return; // Ignore messages from other origins
          }
        } catch {
          return;
        }
      }

      // On WebSocket disconnect, auto-reconnect with a fresh token rather than
      // showing an error. This handles short-lived token expiry transparently.
      if (
        (event.data as { type?: string } | null)?.type ===
        'WEBSOCKET_DISCONNECTED'
      ) {
        if (autoReconnectAttemptsRef.current < MAX_AUTO_RECONNECT_ATTEMPTS) {
          autoReconnectAttemptsRef.current += 1;
          autoReconnectRef.current?.();
        } else {
          setHasError(true);
        }
        return;
      }

      // Check for Streamlit error messages
      if (isStreamlitErrorMessage(event.data)) {
        setHasError(true);
        return;
      }

      // Parse and validate the message
      const message = parseIframeMessage(event.data);
      if (!message) {
        return;
      }

      onMessage?.(message);
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [signedUrl, onMessage]);

  const lastRequestRef = useRef<StreamlitAppSignedUrlRequest | null>(null);
  // Generate signed URL when app is selected
  const generateSignedUrl = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        !selectedAppId ||
        !organizationId ||
        !databaseId ||
        !databaseRevisionId
      ) {
        return;
      }

      const request: StreamlitAppSignedUrlRequest = {
        organizationId,
        databaseId,
        databaseRevisionId,
        streamlitAppId: selectedAppId,
        customVizRequest,
      };

      // Avoid duplicate requests
      if (lastRequestRef.current && isEqual(lastRequestRef.current, request)) {
        return;
      }

      lastRequestRef.current = request;
      setIsLoadingSignedUrl(true);
      setSignedUrl(null);
      setHasError(false);

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: StreamlitAppSignedUrlResponse,
          url: `${getApiBaseUrl()}/external-streamlit-apps/signed-url`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
          },
        });

        if (!isAlive()) {
          return;
        }

        if (result && result.resultType === 'ok') {
          setSignedUrl(result.value.signedUrl);
          autoReconnectAttemptsRef.current = 0;
        }
      } finally {
        if (isAlive()) {
          setIsLoadingSignedUrl(false);
        }
      }
    },
    [
      selectedAppId,
      organizationId,
      databaseId,
      databaseRevisionId,
      customVizRequest,
      userAuthenticatedApiCall,
    ],
  );

  // Call generateSignedUrl when selectedAppId changes
  useEffect(() => {
    if (selectedAppId && organizationId) {
      generateSignedUrl();
    } else {
      setSignedUrl(null);
    }
  }, [selectedAppId, organizationId, generateSignedUrl]);

  const handleReload = useCallback(() => {
    autoReconnectAttemptsRef.current = 0;
    lastRequestRef.current = null; // Bypass duplicate check for explicit reload
    generateSignedUrl();
  }, [generateSignedUrl]);

  useEffect(() => {
    autoReconnectRef.current = () => {
      lastRequestRef.current = null;
      generateSignedUrl();
    };
  }, [generateSignedUrl]);

  return (
    <div className="flex flex-col w-full h-full">
      <Spacing direction="vertical" size="md" className="flex flex-col h-full">
        {isLoadingSignedUrl && selectedAppId && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <CircularProgress size={20} thickness={4} />
            <S2Typography
              variant="body-5"
              textColorVariant="gray-700"
              translatedContent="Loading app..."
            />
          </div>
        )}
        {hasError && !isLoadingSignedUrl && (
          <div className="flex flex-col items-center gap-4 pt-4">
            <S2Typography
              variant="body-5"
              textColorVariant="gray-700"
              translatedContent="Something went wrong loading the app. Please try again."
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: handleReload,
              }}
              variant="primary"
            >
              <S2Typography
                variant="body-5"
                textColorVariant="gray-700"
                translatedContent="Reload"
              />
            </Button>
          </div>
        )}
        {signedUrl && !isLoadingSignedUrl && !hasError && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <iframe
              ref={iframeRef}
              src={signedUrl}
              className="w-full h-full border-0"
              title="Custom Streamlit App"
              allow="fullscreen"
            />
          </div>
        )}
      </Spacing>
    </div>
  );
};
